import React from 'react'
import { CircularProgress, Typography } from '@material-ui/core'
import { withStyles } from '@material-ui/core/styles'

const styles = {
  metricsGrid: {
    display: 'flex',
    alignItems: 'center',
    gap: 24,
    flexWrap: 'wrap',
    padding: '4px 0',
  },
  metricCard: {
    minWidth: 140,
    padding: '8px 12px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  metricGauge: {
    position: 'relative',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 6,
  },
  metricRing: {
    position: 'relative',
    borderRadius: '50%',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  metricRingInner: {
    position: 'absolute',
    borderRadius: '50%',
    backgroundColor: '#ffffff',
  },
  metricGaugeTrack: {
    color: 'rgba(0, 0, 0, 0.12)',
  },
  metricGaugeValue: {
    position: 'absolute',
    left: 0,
  },
  metricValue: {
    position: 'absolute',
    fontSize: 16,
    fontWeight: 600,
    color: '#333333',
  },
  metricLabel: {
    fontSize: 11,
    fontWeight: 600,
    color: '#666666',
    textTransform: 'uppercase',
    textAlign: 'center',
  },
}

const ProjectHealth = ({ projectMetricData = {}, classes }) => {
  const tcinInfo = projectMetricData?.tcin_info || []
  const total = tcinInfo.length

  const pegasusDone = tcinInfo.filter(
    (t) => t.status_by_system?.['PEGASUS']?.system_status === 'COMPLETED'
  ).length
  const pipelineDone = tcinInfo.filter(
    (t) => t.status_by_system?.['PIPELINE']?.system_status === 'COMPLETED'
  ).length
  const assetHubDone = tcinInfo.filter(
    (t) => t.status_by_system?.['ASSETHUB']?.system_status === 'COMPLETED'
  ).length
  const allComplete = tcinInfo.filter(
    (t) => t.overall_status === 'COMPLETED'
  ).length

  const pegasusPct = total ? Math.round((pegasusDone / total) * 100) : 0
  const pipelinePct = total ? Math.round((pipelineDone / total) * 100) : 0
  const assetHubPct = total ? Math.round((assetHubDone / total) * 100) : 0
  const completionPct = total ? Math.round((allComplete / total) * 100) : 0

  const renderMetric = (label, percent, options = {}) => {
    const safePercent = Math.max(0, Math.min(100, percent))
    const size = options.size ?? 84
    const thickness = options.thickness ?? 6
    const color = options.color ?? '#1976d2'
    const showBlend = options.showBlend ?? false
    const blendColors = options.blendColors ?? []
    const degree = Math.round(safePercent * 3.6)
    const ringStyle = showBlend
      ? {
          width: size,
          height: size,
          background: `conic-gradient(${blendColors.join(',')})`,
          WebkitMaskImage: `conic-gradient(#000 0deg, #000 ${degree}deg, transparent ${degree}deg)`,
          maskImage: `conic-gradient(#000 0deg, #000 ${degree}deg, transparent ${degree}deg)`,
        }
      : null
    const innerSize = size - thickness * 2
    const showEmpty = safePercent === 0
    return (
      <div key={label} className={classes.metricCard}>
        <div className={classes.metricGauge}>
          {showBlend ? (
            showEmpty ? (
              <CircularProgress
                variant="determinate"
                value={100}
                size={size}
                thickness={thickness}
                className={classes.metricGaugeTrack}
              />
            ) : (
              <div className={classes.metricRing} style={ringStyle}>
                <div
                  className={classes.metricRingInner}
                  style={{ width: innerSize, height: innerSize }}
                />
              </div>
            )
          ) : (
            <>
              <CircularProgress
                variant="determinate"
                value={100}
                size={size}
                thickness={thickness}
                className={classes.metricGaugeTrack}
              />
              <CircularProgress
                variant="determinate"
                value={safePercent}
                size={size}
                thickness={thickness}
                className={classes.metricGaugeValue}
                style={{ color }}
              />
            </>
          )}
          <Typography className={classes.metricValue}>
            {safePercent}%
          </Typography>
        </div>
        <Typography className={classes.metricLabel}>{label}</Typography>
      </div>
    )
  }

  return (
    <div className={classes.metricsGrid}>
      {renderMetric('Overall Completion', completionPct, {
        size: 92,
        thickness: 7,
      })}
      {renderMetric('Pegasus', pegasusPct, {
        size: 80,
        thickness: 6,
        color: '#1e88e5',
      })}
      {renderMetric('Pipeline', pipelinePct, {
        size: 80,
        thickness: 6,
        color: '#fb8c00',
      })}
      {renderMetric('AssetHub', assetHubPct, {
        size: 80,
        thickness: 6,
        color: '#8e24aa',
      })}
    </div>
  )
}

export default withStyles(styles)(React.memo(ProjectHealth))
